import classNames from "classnames";
import dayjs from "dayjs";
import AscCircularProgressBar from "../../AscCircularProgressBar/AscCircularProgressBar";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscChallengeTimeStatsCard.module.scss";
import { useEffect, useState } from "react";
import { CalendarEmptyIcon } from "../../AscIcons";
import { calculateTimeLeft } from "@ascendnow/shared";

export interface AscChallengeTimeStatsCardProps {
  deadlineDate: string;
  estimatedTimeInHours: number;
  className?: string;
  loading?: boolean;
}

export default function AscChallengeTimeStatsCard({
  deadlineDate,
  estimatedTimeInHours,
  className,
  loading = false,
}: AscChallengeTimeStatsCardProps) {
  const [values, setValues] = useState({
    timeValue: 0,
    timeUnit: "seconds",
    completedPercentage: 0,
  });
  const { timeValue, timeUnit, completedPercentage } = values;
  
  function calculateValues(estimatedTimeInHours: number, deadlineDate: string) {
    const remainingTimeInHours = dayjs(deadlineDate).diff(
      dayjs(),
      "hours",
      true
    );
    const completedPercentage =
      ((estimatedTimeInHours - remainingTimeInHours) /
        (estimatedTimeInHours || 1)) *
      100;

    if (completedPercentage >= 100) {
      return {
        timeValue: 0,
        timeUnit: "seconds",
        completedPercentage,
      };
    }
    
    const { value, unit } = calculateTimeLeft(deadlineDate);

    return {
      timeValue: value,
      timeUnit: unit,
      completedPercentage,
    };
  }

  useEffect(() => {
    const values = calculateValues(estimatedTimeInHours, deadlineDate);
    setValues(values);
    const interval = setInterval(() => {
      const values = calculateValues(estimatedTimeInHours, deadlineDate);
      setValues(values);
    }, 1000);
    if (values.completedPercentage >= 100) {
      return () => clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [estimatedTimeInHours, deadlineDate]);

  return (
    <AscGenericCard
      className={classNames(styles.ground, className)}
      bodyClassName={styles.cardBody}
      contentClassName={styles.cardContent}
      title={null}
      coverImageUrl={null}
      loading={loading}
    >
      <div className={styles.leftContent}>
        <div className={styles.remainingTimeContainer}>
          <div className={styles.remainingTime}>{timeValue}</div>
          <div className={styles.remainingTimeLabel}>
            {timeUnit} <br />
            left
          </div>
        </div>
        <div className={styles.deadlineContainer}>
          <CalendarEmptyIcon className={styles.deadlineIcon} />
          <span className={styles.deadlineDare}>
            {dayjs(deadlineDate).format("h:mm A, D MMMM YYYY")}
          </span>
        </div>
      </div>
      <AscCircularProgressBar
        className={styles.progressBar}
        percentage={completedPercentage}
      />
    </AscGenericCard>
  );
}
